// @file Store for all whiteboard upload state and logic in Sandbox
import { __ } from '@@/bits/intl'
import { FILE_TOO_LARGE_ERROR, startUpload, type UploadJob } from '@@/bits/uploader'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { uniqueId } from 'lodash-es'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useWhiteboardUploadStore = defineStore('whiteboardUpload', () => {
  const surfaceStore = useSurfaceStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const fileThatExceededSize = ref()

  /**
   * ATTACHMENTS
   */

  type UploadKey = string
  interface Upload {
    uploadKey: UploadKey
    fileName: string
    progress: number | null
    done: boolean
    type: 'file' | 'link'
    uploadJob?: UploadJob
  }
  const uploads = ref<Record<UploadKey, Upload>>({})
  const progressTimerById = ref<Record<UploadKey, ReturnType<typeof setInterval>>>({})
  const ONE_MB = 1024 * 1024

  function getNextUploadKey(): UploadKey {
    return uniqueId('upload-')
  }

  function startUploading(file: File, uploadKey: UploadKey, uploadJob?: UploadJob): void {
    const initialUploadProgress = 0.3
    uploads.value[uploadKey] = {
      uploadKey,
      fileName: file.name,
      progress: initialUploadProgress,
      done: false,
      type: 'file',
      uploadJob,
    }
    uploads.value = { ...uploads.value }
    const fileSize = file.size
    // Calculate expected time and upload based on that
    const expectedTime = fileSize !== null ? fileSize / (4 * ONE_MB) + 4 : 1
    let timeSpent = 0
    const progressTimer = setInterval(() => {
      timeSpent += 1
      if (timeSpent >= expectedTime) {
        clearInterval(progressTimer)
        uploads.value[uploadKey].progress = 1
        uploads.value = { ...uploads.value }
        return
      }
      const uploadProgress = initialUploadProgress + (timeSpent / expectedTime) * 0.7
      uploads.value[uploadKey].progress = uploadProgress
      uploads.value = { ...uploads.value }
    }, 1000)

    progressTimerById.value[uploadKey] = progressTimer
  }

  function finishUploading(uploadKey: UploadKey): void {
    clearInterval(progressTimerById.value[uploadKey])
    uploads.value[uploadKey].progress = null
    uploads.value[uploadKey].done = true
    uploads.value = { ...uploads.value }
    fileThatExceededSize.value = null
  }

  function cancelUpload(uploadKey: UploadKey): void {
    clearInterval(progressTimerById.value[uploadKey])
    const uploadJob = uploads.value[uploadKey].uploadJob
    if (uploadJob != null) {
      uploadJob.cancel()
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete uploads.value[uploadKey]
    uploads.value = { ...uploads.value }
  }

  function startUploadingLink(link: string, uploadKey: UploadKey): void {
    uploads.value[uploadKey] = {
      uploadKey,
      fileName: link,
      progress: null,
      done: false,
      type: 'link',
    }
    uploads.value = { ...uploads.value }
  }

  function startNewFileUpload(file: File): UploadKey {
    const uploadJob = startUpload(file, { maxFileSize: surfaceStore.uploadLimit })
    const uploadKey = getNextUploadKey()
    startUploading(file, uploadKey, uploadJob)
    uploadJob.on('start', () => {
      startUploading(file, uploadKey)
    })
    uploadJob.on('done', (payload) => {
      finishUploading(uploadKey)
      useSurfacePostsStore().createPostImmediately({
        attributes: {
          attachment: payload.url,
          wish_content: {
            attachment_props: {
              url: payload.url,
            },
            is_processed: false,
          },
        },
      })
    })
    uploadJob.on('error', (response) => {
      cancelUpload(uploadKey)

      if (response.error === FILE_TOO_LARGE_ERROR) {
        fileThatExceededSize.value = file
        surfaceStore.showOverFileSizeModal({ file })
      } else {
        globalAlertDialogStore.openAlertDialog({
          title: __('There was an error loading the file'),
          body: __('Sorry, there was an error uploading your file.'),
        })
      }
    })
    return uploadKey
  }

  function startNewUrlUpload(url: string, options?: { altText?: string }): UploadKey {
    const uploadKey = url
    const fileName = url.split('/').pop() ?? url
    startUploadingLink(options?.altText ?? fileName, uploadKey)
    useSurfacePostsStore().createPostImmediately({
      attributes: {
        attachment: url,
        wish_content: {
          attachment_props: {
            url,
            alternative_text: options?.altText,
          },
          is_processed: false,
        },
      },
    })
    return uploadKey
  }
  return {
    /* ATTACHMENT */
    startUploading,
    startUploadingLink,
    finishUploading,
    cancelUpload,
    uploads,
    getNextUploadKey,
    startNewFileUpload,
    startNewUrlUpload,
    fileThatExceededSize,
  }
})
